/**
 * @file The dashboard.
 *
 * Section order is deliberate, and it is the thing this file is most likely to be
 * changed over:
 *
 *   0. **Finish setting up** — the onboarding ramp, and only while there is a step
 *      left (`setup.next !== null`). It sits ABOVE everything because its whole job
 *      is to be the first thing a landlord who has just signed up reads; it
 *      disappears once the first cycle is complete, because setup guidance that
 *      never retires is furniture. `SetupRamp.tsx` owns it.
 *   1. **Portfolio** — the numbers that are real today (properties, units, beds,
 *      baths).
 *   2. **Money** — the quick glance a landlord opens the app for: occupancy, rent
 *      due, collected, outstanding. **All four are real** (T1.5): leases, rent
 *      charges and the ledger all exist, so the figures are computed server-side
 *      (`GET /overview` → `money`) and rendered as measurements rather than
 *      placeholders.
 *   3. **Needs your attention** — the action list, deliberately LAST, where the
 *      property list used to be. It is a list of things to do rather than a copy
 *      of the Properties page: a dashboard that repeats a page tells a landlord
 *      nothing they did not already know.
 *
 * ## The action list is real, and it is FIVE categories
 *
 * This section used to carry three disabled rows that each said "Soon" and named a
 * dependency — overdue rent needed rent charges, maintenance requests needed
 * maintenance, "leases expiring" needed leases. **All three of those surfaces
 * shipped** (T1.4, T1.6, T1.2), so the rows became lies: the home screen of the app
 * was telling a landlord that built features were missing. They are now computed
 * counts from `attention`, each one the same number the page it links to shows, and
 * `PendingAction` is gone because there is nothing left that cannot be computed.
 *
 * **"Leases expiring" was renamed, deliberately.** What the platform can compute is
 * the turnover classification: a unit whose tenancy is winding down, whose next term
 * is already signed, or which nothing is in force on. It cannot compute "expiring
 * within 30 days" — no notice period and no pre-leasing window exists anywhere in
 * this product, and picking one would be inventing founder-owned policy on a
 * dashboard. So the row is named after what it counts ("Units in transition") and
 * links to the page that shows the timeline, rather than bending the number to fit
 * the old label.
 *
 * **`unitsWithoutLease` is the fifth, and it is not part of the ramp.** The ramp
 * above teaches the first cycle once; this row is what catches the landlord who adds
 * a second building later and leaves a unit unleased — the reported case of one
 * property, two units and no leases, which the dashboard used to answer with "All
 * clear". It is the SAME rule the Turnover page and a unit's own page use
 * (`LIVE_LEASE_STATUSES` through `currentLiveLeaseId`), computed once on the server,
 * so no two screens can disagree about whether a unit is earning. Its link is the
 * lease step's target — that descriptor IS "the first unit with no tenancy in force,
 * or the units' own page" — so the row goes to the specific unit where there is one.
 *
 * ## The rule that survived the placeholders: never a fabricated zero
 *
 * A metric whose underlying row set is EMPTY renders `—` with a sentence saying so,
 * not `$0.00`. The difference is the whole reason the placeholders existed: `$0
 * collected` reads as "nobody paid", which is a claim about the world, while "No
 * payments recorded yet" is a fact about this portfolio. `$0.00` IS rendered when
 * there are real rows and they genuinely sum to nothing (every charge settled, no
 * unpaid period past its due date) — that is arithmetic, and the hint says which.
 *
 * An ACTION with a zero count is not rendered at all, and that is the same rule in
 * the other direction: a row reading "0 overdue" would be a claim that nothing is
 * owed rather than a task, and an action list with nothing to do says so once, in
 * the "All clear" card.
 */
import { Link } from "@tanstack/react-router";
import {
  Banknote,
  Building2,
  CalendarClock,
  CheckCircle2,
  ChevronRight,
  DoorOpen,
  Hammer,
  type LucideIcon,
} from "lucide-react";
import { centsToDollarString } from "@neighboros/shared";
import { useOverview } from "../api/queries";
import type { ApiWorkspace } from "../api/types";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { SetupRamp, TargetLink } from "./SetupRamp";
import { CenteredPanel, ErrorPanel, Metric, Spinner } from "./ui";

/** Integer cents, as money. The one formatting rule for every figure here. */
function formatMoney(cents: number): string {
  return `$${centsToDollarString(cents)}`;
}

/** `1 charge` / `2 charges` — a count that reads as English. */
function plural(count: number, one: string, many: string): string {
  return `${String(count)} ${count === 1 ? one : many}`;
}

/**
 * One real action: the count the API computed, and a link to the page that shows
 * the rows behind it.
 *
 * The styling is the one the property action below uses, so an action looks like an
 * action whatever produced it — and the whole row is the link, so acting on it is
 * one click.
 */
function AttentionAction({
  icon: Icon,
  title,
  detail,
  to,
  params,
}: {
  readonly icon: LucideIcon;
  readonly title: string;
  readonly detail: string;
  readonly to:
    | "/workspaces/$workspaceId/rent-charges"
    | "/workspaces/$workspaceId/maintenance"
    | "/workspaces/$workspaceId/turnover";
  readonly params: { readonly workspaceId: string };
}) {
  return (
    <li>
      <Link
        to={to}
        params={params}
        className="flex items-center gap-3 rounded-lg border border-primary/40 bg-primary/5 px-4 py-3 hover:border-primary"
      >
        <Icon className="size-4 shrink-0 text-primary" aria-hidden="true" />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-medium">{title}</span>
          <span className="block text-xs text-muted-foreground">{detail}</span>
        </span>
        <ChevronRight className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
      </Link>
    </li>
  );
}

export function DashboardScreen({ workspace }: { readonly workspace: ApiWorkspace }) {
  const overview = useOverview(workspace.id);

  if (overview.status === "pending") {
    return (
      <CenteredPanel>
        <Spinner />
        Loading your portfolio…
      </CenteredPanel>
    );
  }
  if (overview.status === "error") {
    return (
      <ErrorPanel
        error={overview.error}
        onRetry={() => {
          void overview.refetch();
        }}
      />
    );
  }

  const { counts, properties, money, attention, setup } = overview.data;
  const withoutUnits = properties.filter((property) => property.unitCount === 0);

  /**
   * Where the unleased-units action goes.
   *
   * The `lease` step's `target` IS the server's answer to "the first unit with no
   * tenancy in force, or the lists when there is nothing specific" — computed once,
   * in the same transaction as `attention.unitsWithoutLease`. Reusing that descriptor
   * rather than asking the API for the same id twice is what keeps the action row and
   * the ramp from being able to point at two different units.
   */
  const unleasedTarget =
    setup.steps.find((step) => step.key === "lease")?.target ?? { kind: "properties" };

  /**
   * Occupancy, as a whole percent.
   *
   * `null` when there are no units at all: `0%` would be a claim about the world —
   * "nothing is rented" — when the truth is that there is nothing to rent yet.
   */
  const occupancyPercent =
    money.totalUnits === 0
      ? null
      : Math.round((money.leasedUnits / money.totalUnits) * 100);

  /**
   * Nothing to do at all — the ONE thing the "All clear" card is allowed to say.
   *
   * Every category the action list carries is folded into this condition, so a new
   * action cannot be added without deciding whether it counts as "all clear".
   */
  const allClear =
    withoutUnits.length === 0 &&
    attention.unitsWithoutLease === 0 &&
    attention.overdueCharges === 0 &&
    attention.openMaintenance === 0 &&
    attention.unitsInTransition === 0;

  return (
    <div className="flex flex-col gap-6">
      {/*
        FIRST, and only while the first cycle is unfinished. The card renders nothing
        at all once `setup.next` is null — this is guidance, not a permanent panel.
      */}
      <SetupRamp workspaceId={workspace.id} setup={setup} />

      <header className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold tracking-tight">Dashboard</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {workspace.name} · {counts.properties}{" "}
            {counts.properties === 1 ? "property" : "properties"}, {counts.units}{" "}
            {counts.units === 1 ? "unit" : "units"}
          </p>
        </div>
        <Button asChild>
          <Link to="/workspaces/$workspaceId/properties" params={{ workspaceId: workspace.id }}>
            Manage properties
          </Link>
        </Button>
      </header>

      <section aria-labelledby="portfolio-heading" className="flex flex-col gap-3">
        <h2 id="portfolio-heading" className="text-lg font-semibold tracking-tight">
          Portfolio
        </h2>
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <Metric label="Properties" value={String(counts.properties)} />
          <Metric label="Units" value={String(counts.units)} />
          <Metric label="Bedrooms" value={String(counts.beds)} />
          <Metric
            label="Bathrooms"
            value={counts.baths === 0 ? "0" : counts.baths.toFixed(1)}
          />
        </div>
      </section>

      <section aria-labelledby="money-heading" className="flex flex-col gap-3">
        <h2 id="money-heading" className="text-lg font-semibold tracking-tight">
          Money
        </h2>
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          {/*
            Four real figures, each folded server-side from rows that exist. Each
            renders `—` rather than `$0.00` when its underlying set is EMPTY, because
            "$0 collected" over no payments reads as "nobody paid" — a claim about the
            world — while "No payments recorded yet" is a fact about this portfolio.
          */}
          <Metric
            label="Occupancy"
            value={occupancyPercent === null ? "—" : `${String(occupancyPercent)}%`}
            hint={
              occupancyPercent === null
                ? "No units yet"
                : `${String(money.leasedUnits)} of ${String(money.totalUnits)} units leased`
            }
          />
          <Metric
            label="Rent due"
            value={money.charges === 0 ? "—" : formatMoney(money.dueCents)}
            hint={
              money.charges === 0
                ? "No charges generated yet"
                : money.dueCents === 0
                  ? "No unpaid charge has passed its due date"
                  : "Unpaid rent past its due date"
            }
          />
          <Metric
            label="Collected"
            value={money.payments === 0 ? "—" : formatMoney(money.collectedCents)}
            hint={
              money.payments === 0
                ? "No payments recorded yet"
                : money.pendingCents > 0
                  ? `plus ${formatMoney(money.pendingCents)} pending`
                  : `${String(money.payments)} ${
                      money.payments === 1 ? "payment" : "payments"
                    } settled`
            }
          />
          <Metric
            label="Outstanding"
            value={money.charges === 0 ? "—" : formatMoney(money.outstandingCents)}
            hint={
              money.charges === 0
                ? "No charges generated yet"
                : `Across ${String(money.charges)} ${
                    money.charges === 1 ? "charge" : "charges"
                  }`
            }
          />
        </div>
      </section>

      {/* LAST, and a list of things to do — not a copy of the Properties page. */}
      <section aria-labelledby="attention-heading" className="flex flex-col gap-3">
        <h2 id="attention-heading" className="text-lg font-semibold tracking-tight">
          Needs your attention
        </h2>

        {allClear ? (
          <Card className="flex-row items-center gap-3 px-4 py-4">
            <CheckCircle2 className="size-5 shrink-0 text-primary" aria-hidden="true" />
            <div>
              <p className="text-sm font-medium">All clear</p>
              <p className="text-sm text-muted-foreground">Nothing needs doing right now.</p>
            </div>
          </Card>
        ) : null}

        <ul className="flex flex-col gap-2">
          {withoutUnits.map((property) => (
            <li key={property.id}>
              <Link
                to="/workspaces/$workspaceId/properties/$propertyId"
                params={{ workspaceId: workspace.id, propertyId: property.id }}
                className="flex items-center gap-3 rounded-lg border border-primary/40 bg-primary/5 px-4 py-3 hover:border-primary"
              >
                <Building2 className="size-4 shrink-0 text-primary" aria-hidden="true" />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium">
                    {property.label ?? property.addressLine1} has no units
                  </span>
                  <span className="block text-xs text-muted-foreground">
                    A lease attaches to a unit, so this cannot be rented out yet.
                  </span>
                </span>
                <ChevronRight
                  className="size-4 shrink-0 text-muted-foreground"
                  aria-hidden="true"
                />
              </Link>
            </li>
          ))}

          {/*
            The first of the COUNTED categories, and the one that outlives the ramp: a
            unit nothing is in force on earns nothing, and this is where a landlord who
            added a second building and left a unit empty finds out. The count is the
            API's own — the SAME rule the Turnover page's `withoutActiveLease` and a
            unit's own page use — and the link is the `lease` step's target, which is
            exactly "the first unit with no tenancy in force, or the units' list".
          */}
          {attention.unitsWithoutLease > 0 ? (
            <li>
              <TargetLink
                workspaceId={workspace.id}
                target={unleasedTarget}
                className="flex items-center gap-3 rounded-lg border border-primary/40 bg-primary/5 px-4 py-3 hover:border-primary"
              >
                <DoorOpen className="size-4 shrink-0 text-primary" aria-hidden="true" />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium">
                    {plural(attention.unitsWithoutLease, "unit", "units")} without a lease
                  </span>
                  <span className="block text-xs text-muted-foreground">
                    No tenancy is in force on{" "}
                    {attention.unitsWithoutLease === 1 ? "it" : "them"}, so{" "}
                    {attention.unitsWithoutLease === 1 ? "it earns" : "they earn"} nothing.
                  </span>
                </span>
                <ChevronRight
                  className="size-4 shrink-0 text-muted-foreground"
                  aria-hidden="true"
                />
              </TargetLink>
            </li>
          ) : null}

          {/*
            One row per category with something in it. The count is the API's own
            number and the amount is `money.dueCents` — the same figure the Money
            section above renders, so the two cannot disagree. A row whose count is
            zero is ABSENT: "0 overdue" is a claim about the world, and the "All
            clear" card is where an empty action list says so.
          */}
          {attention.overdueCharges > 0 ? (
            <AttentionAction
              icon={Banknote}
              title="Overdue rent"
              detail={`${formatMoney(money.dueCents)} across ${plural(
                attention.overdueCharges,
                "charge",
                "charges",
              )}`}
              to="/workspaces/$workspaceId/rent-charges"
              params={{ workspaceId: workspace.id }}
            />
          ) : null}

          {attention.openMaintenance > 0 ? (
            <AttentionAction
              icon={Hammer}
              title="Maintenance requests"
              detail={
                attention.emergencyMaintenance > 0
                  ? `${plural(attention.openMaintenance, "open request", "open requests")} · ${plural(
                      attention.emergencyMaintenance,
                      "emergency",
                      "emergencies",
                    )}`
                  : `${plural(attention.openMaintenance, "open request", "open requests")}`
              }
              to="/workspaces/$workspaceId/maintenance"
              params={{ workspaceId: workspace.id }}
            />
          ) : null}

          {attention.unitsInTransition > 0 ? (
            <AttentionAction
              icon={CalendarClock}
              title="Units in transition"
              detail={`${plural(attention.unitsInTransition, "unit", "units")} with a tenancy ending, nothing in force, or the next one signed`}
              to="/workspaces/$workspaceId/turnover"
              params={{ workspaceId: workspace.id }}
            />
          ) : null}
        </ul>
      </section>

      {/*
        AFTER the action list, and ABSENT at zero (the same rule as every action row).
        A heads-up, not a task, so it is deliberately not part of `allClear`. The
        count and rows are the API's: `endDate` is a plain calendar date and is
        printed as it is (never parsed as an instant), and `daysLeft` is already the
        building's own calendar-day difference, so nothing here does date arithmetic.
      */}
      {attention.leasesEndingSoon.count > 0 ? (
        <section aria-labelledby="leases-ending-heading" className="flex flex-col gap-3">
          <h2 id="leases-ending-heading" className="text-lg font-semibold tracking-tight">
            Leases ending soon
          </h2>
          <p className="text-sm text-muted-foreground">
            {plural(attention.leasesEndingSoon.count, "active lease ends", "active leases end")}{" "}
            within 60 days.
          </p>
          <ul className="flex flex-col gap-2">
            {attention.leasesEndingSoon.items.map((lease) => (
              <li key={lease.leaseId}>
                <Link
                  to="/workspaces/$workspaceId/leases/$leaseId"
                  params={{ workspaceId: workspace.id, leaseId: lease.leaseId }}
                  className="flex items-center gap-3 rounded-lg border border-primary/40 bg-primary/5 px-4 py-3 hover:border-primary"
                >
                  <CalendarClock className="size-4 shrink-0 text-primary" aria-hidden="true" />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium">
                      {lease.propertyLabel === null
                        ? lease.unitLabel
                        : `${lease.propertyLabel} · ${lease.unitLabel}`}
                    </span>
                    <span className="block text-xs text-muted-foreground">
                      Ends {lease.endDate} · {daysLeftText(lease.daysLeft)}
                    </span>
                  </span>
                  <ChevronRight
                    className="size-4 shrink-0 text-muted-foreground"
                    aria-hidden="true"
                  />
                </Link>
              </li>
            ))}
          </ul>
          {attention.leasesEndingSoon.count > attention.leasesEndingSoon.items.length ? (
            <p className="text-xs text-muted-foreground">
              Showing the soonest {String(attention.leasesEndingSoon.items.length)} of{" "}
              {String(attention.leasesEndingSoon.count)}.
            </p>
          ) : null}
        </section>
      ) : null}
    </div>
  );
}

/** `today` / `in 1 day` / `in 12 days`, from the server's calendar-day difference. */
function daysLeftText(daysLeft: number): string {
  if (daysLeft <= 0) return "today";
  return `in ${plural(daysLeft, "day", "days")}`;
}
